/**
 * useUnreadCountByType - Hook para obter contador de não lidas por tipo
 * Usado nos badges das tabs da sidebar
 */

import { useQuery } from '@tanstack/react-query';
import { chatService } from '../services';

export interface UseUnreadCountByTypeOptions {
  /** Tipo de conversa para filtrar */
  conversationType: 'individual' | 'group' | 'ai';
  /** Habilitar/desabilitar query */
  enabled?: boolean;
  /** Intervalo de refetch (padrão: 30 segundos) */
  refetchInterval?: number;
}

/**
 * Hook para obter contador de não lidas por tipo de conversa
 *
 * @example
 * ```tsx
 * const { data: unreadCount } = useUnreadCountByType({
 *   conversationType: 'individual'
 * });
 *
 * // Exibir badge
 * {unreadCount > 0 && <Badge>{unreadCount}</Badge>}
 * ```
 */
export const useUnreadCountByType = (options: UseUnreadCountByTypeOptions) => {
  const {
    conversationType,
    enabled = true,
    refetchInterval = 30000 // 30 segundos
  } = options;

  return useQuery({
    queryKey: ['conversations', 'unread-count', conversationType],
    queryFn: () => chatService.getUnreadCountByType(conversationType),
    enabled,
    staleTime: 10 * 1000, // 10 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval, // Auto-refetch every 30 seconds
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });
};
